export interface DashboardAnalyticsJob<T = unknown> {
  key: string;
  run: () => Promise<T>;
  onSuccess: (value: T) => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
}

export async function loadDashboardAnalyticsIndependently(
  jobs: DashboardAnalyticsJob[],
): Promise<number> {
  const results = await Promise.allSettled(
    jobs.map(async (job) => {
      try {
        const value = await job.run();
        job.onSuccess(value);
      } catch (error) {
        job.onError?.(error);
        throw error;
      } finally {
        job.onSettled?.();
      }
    }),
  );
  return results.filter((result) => result.status === 'rejected').length;
}

export function sanitizeDashboardWorkspaceOrder<T extends string>(
  candidate: readonly unknown[],
  defaults: readonly T[],
): T[] {
  const valid = candidate.filter((key): key is T => typeof key === 'string' && defaults.includes(key as T));
  if (valid.length !== defaults.length || new Set(valid).size !== defaults.length) {
    return [...defaults];
  }
  return valid;
}

export function selectRecentDashboardActivities<T extends { createdAt?: string }>(
  activities: readonly T[],
  limit: number,
): T[] {
  return activities
    .filter((activity) => Boolean(activity.createdAt))
    .sort((a: T, b: T) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    .slice(0, limit);
}

export function selectDashboardSalesAmount(
  stats?: { signedAmount?: number | null } | null,
): number {
  return Number(stats?.signedAmount || 0);
}

export function selectScopedRecentDashboardActivities<T extends { createdAt?: string }>(
  activities: readonly T[],
  isRelated: (activity: T) => boolean,
  limit: number,
): T[] {
  return selectRecentDashboardActivities(activities.filter(isRelated), limit);
}

export function shouldLoadDashboardCoreUsers(hasPermission: (code: string) => boolean): boolean {
  return hasPermission('sys.user.list');
}

export function createSingleFlight<TArgs extends unknown[], TResult>(
  operation: (...args: TArgs) => Promise<TResult>,
): (...args: TArgs) => Promise<TResult> {
  let inflight: Promise<TResult> | null = null;
  return (...args) => {
    if (!inflight) {
      inflight = operation(...args).finally(() => {
        inflight = null;
      });
    }
    return inflight;
  };
}
